/**
 * Virtual joystick for touch-device camera movement.
 *
 * Ported from the sparkjsdev/spark mobile-joystick example
 * (https://github.com/sparkjsdev/spark/tree/main/examples/mobile-joystick) and
 * wrapped in a class so SparkViewer can create/dispose one per mount without
 * leaking DOM nodes or event listeners across React renders.
 *
 * Usage:
 *   if (isMobileDevice()) {
 *     const stick = new MobileJoystick();
 *     // in render loop:
 *     const { x, y, active } = stick.getInput();
 *     // x: -1..1 strafe (left/right)
 *     // y: -1..1 forward/back (positive = backward to match screen-space)
 *     // ... apply to camera ...
 *     // on unmount:
 *     stick.dispose();
 *   }
 */

export interface JoystickInput {
  x: number;
  y: number;
  active: boolean;
}

export interface MobileJoystickOptions {
  joystickSize?: number;
  knobSize?: number;
  deadzone?: number;
  marginLeft?: number;
  marginBottom?: number;
  outerColor?: string;
  outerBorder?: string;
  knobColor?: string;
  knobActiveColor?: string;
}

const DEFAULTS: Required<MobileJoystickOptions> = {
  joystickSize: 120,
  knobSize: 50,
  deadzone: 0.15,
  marginLeft: 30,
  marginBottom: 30,
  outerColor: "rgba(255, 255, 255, 0.2)",
  outerBorder: "rgba(255, 255, 255, 0.4)",
  knobColor: "rgba(255, 255, 255, 0.5)",
  knobActiveColor: "rgba(100, 150, 255, 0.7)",
};

/** Detect touch-style devices, excluding VR headsets which present as touch. */
export function isMobileDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  if (/OculusBrowser|Quest|Oculus/i.test(navigator.userAgent)) return false;
  return (
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  );
}

export class MobileJoystick {
  private readonly config: Required<MobileJoystickOptions>;
  private container: HTMLDivElement;
  private knob: HTMLDivElement;
  private activeTouchId: number | null = null;
  private center = { x: 0, y: 0 };
  private input: JoystickInput = { x: 0, y: 0, active: false };
  private enabled = true;
  private disposed = false;

  constructor(options: MobileJoystickOptions = {}) {
    this.config = { ...DEFAULTS, ...options };
    const { container, knob } = this.buildUi();
    this.container = container;
    this.knob = knob;
    document.body.appendChild(this.container);
    this.updateCenter();
    this.attachListeners();
  }

  getInput(): JoystickInput {
    if (!this.enabled || this.disposed) return { x: 0, y: 0, active: false };
    return { ...this.input };
  }

  setEnabled(value: boolean): void {
    this.enabled = value;
    this.container.style.display = value ? "block" : "none";
    if (!value) this.input = { x: 0, y: 0, active: false };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("orientationchange", this.handleOrientationChange);
    this.container.removeEventListener("touchstart", this.handleTouchStart);
    document.removeEventListener("touchmove", this.handleTouchMove);
    document.removeEventListener("touchend", this.handleTouchEnd);
    document.removeEventListener("touchcancel", this.handleTouchEnd);
    this.container.remove();
  }

  // ── internals ─────────────────────────────────────────────────────────

  private buildUi(): { container: HTMLDivElement; knob: HTMLDivElement } {
    const c = this.config;
    const container = document.createElement("div");
    container.dataset.role = "mobile-joystick";
    container.style.cssText = `
      position: fixed;
      left: ${c.marginLeft}px;
      bottom: ${c.marginBottom}px;
      width: ${c.joystickSize}px;
      height: ${c.joystickSize}px;
      z-index: 1000;
      touch-action: none;
      pointer-events: auto;
    `;

    const outer = document.createElement("div");
    outer.style.cssText = `
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background: ${c.outerColor};
      border: 2px solid ${c.outerBorder};
      box-sizing: border-box;
    `;

    const knob = document.createElement("div");
    knob.style.cssText = `
      position: absolute;
      width: ${c.knobSize}px;
      height: ${c.knobSize}px;
      border-radius: 50%;
      background: ${c.knobColor};
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      transition: background 0.1s;
    `;

    outer.appendChild(knob);
    container.appendChild(outer);
    return { container, knob };
  }

  private updateCenter = (): void => {
    const rect = this.container.getBoundingClientRect();
    this.center = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  };

  private attachListeners(): void {
    window.addEventListener("resize", this.handleResize);
    window.addEventListener("orientationchange", this.handleOrientationChange);
    this.container.addEventListener("touchstart", this.handleTouchStart, { passive: false });
    document.addEventListener("touchmove", this.handleTouchMove, { passive: false });
    document.addEventListener("touchend", this.handleTouchEnd, { passive: false });
    document.addEventListener("touchcancel", this.handleTouchEnd, { passive: false });
  }

  private handleResize = (): void => this.updateCenter();
  private handleOrientationChange = (): void => {
    setTimeout(this.updateCenter, 100);
  };

  private handleTouchStart = (e: TouchEvent): void => {
    if (!this.enabled || this.activeTouchId !== null) return;
    const touch = e.changedTouches[0];
    this.activeTouchId = touch.identifier;
    this.updateCenter();
    this.updateKnob(touch.clientX, touch.clientY);
    this.knob.style.background = this.config.knobActiveColor;
    this.input.active = true;
    e.preventDefault();
  };

  private handleTouchMove = (e: TouchEvent): void => {
    if (!this.enabled || this.activeTouchId === null) return;
    for (let i = 0; i < e.changedTouches.length; i++) {
      const t = e.changedTouches[i];
      if (t.identifier === this.activeTouchId) {
        this.updateKnob(t.clientX, t.clientY);
        e.preventDefault();
        break;
      }
    }
  };

  private handleTouchEnd = (e: TouchEvent): void => {
    if (this.activeTouchId === null) return;
    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === this.activeTouchId) {
        this.reset();
        break;
      }
    }
  };

  private updateKnob(touchX: number, touchY: number): void {
    const c = this.config;
    const maxRadius = (c.joystickSize - c.knobSize) / 2;
    let dx = touchX - this.center.x;
    let dy = touchY - this.center.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance > maxRadius) {
      dx = (dx / distance) * maxRadius;
      dy = (dy / distance) * maxRadius;
    }
    this.knob.style.left = `${50 + (dx / maxRadius) * 50}%`;
    this.knob.style.top = `${50 + (dy / maxRadius) * 50}%`;

    let nx = dx / maxRadius;
    let ny = dy / maxRadius;
    if (Math.abs(nx) < c.deadzone) nx = 0;
    if (Math.abs(ny) < c.deadzone) ny = 0;
    this.input.x = nx;
    this.input.y = ny;
  }

  private reset(): void {
    this.activeTouchId = null;
    this.input = { x: 0, y: 0, active: false };
    this.knob.style.left = "50%";
    this.knob.style.top = "50%";
    this.knob.style.background = this.config.knobColor;
  }
}
